import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getConsent,
  saveConsent,
  OPEN_CONSENT_SETTINGS_EVENT,
} from '../consent';
import { initGA, trackPageView, updateAnalyticsConsent } from '../analytics';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  &.modal {
    align-items: center;
    padding: 1rem;
  }
`;

const Banner = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 1rem 1.5rem;
  background: var(--card-background);
  border: 1px solid rgba(198, 198, 198, 0.15);
  border-top: 4px solid var(--primary-color);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  padding: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    margin: 0 0.75rem 0.75rem;
    padding: 1.25rem;
  }
`;

const TextBlock = styled.div`
  flex: 1 1 400px;
`;

const Title = styled.h2`
  font-size: 1.1rem;
  color: var(--text-color);
  margin: 0 0 0.5rem;
`;

const Description = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-color);
  opacity: 0.85;
  margin: 0;

  a {
    color: var(--primary-color);
    text-decoration: underline;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const BaseButton = styled.button`
  cursor: pointer;
  border-radius: 8px;
  padding: 0.7rem 1.3rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
`;

const PrimaryButton = styled(BaseButton)`
  background: var(--primary-color);
  color: #fff;

  &:hover {
    filter: brightness(1.1);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  border-color: var(--secondary-color);
  color: var(--text-color);

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const TextButton = styled(BaseButton)`
  background: transparent;
  color: var(--text-color);
  opacity: 0.8;
  text-decoration: underline;
  padding: 0.7rem 0.4rem;

  &:hover {
    opacity: 1;
    color: var(--primary-color);
  }
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--card-background);
  border-top: 4px solid var(--primary-color);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  padding: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.4rem;
  color: var(--text-color);
  margin: 0 0 1rem;
`;

const ModalText = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-color);
  opacity: 0.85;
  margin: 0 0 1.5rem;
`;

const CategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(198, 198, 198, 0.15);

  &:last-of-type {
    border-bottom: none;
  }
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryTitle = styled.h3`
  font-size: 1rem;
  color: var(--text-color);
  margin: 0 0 0.3rem;
`;

const CategoryText = styled.p`
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--text-color);
  opacity: 0.75;
  margin: 0;
`;

const Switch = styled.label<{ $disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const Slider = styled.span<{ $checked: boolean }>`
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: ${({ $checked }) => ($checked ? 'var(--primary-color)' : 'var(--secondary-color)')};
  border-radius: 999px;
  transition: 0.2s;

  &::before {
    content: '';
    position: absolute;
    height: 20px;
    width: 20px;
    left: ${({ $checked }) => ($checked ? '23px' : '3px')};
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.2s;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const ConsentManager: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      if (existing.analytics) {
        updateAnalyticsConsent(true);
        initGA();
        trackPageView(window.location.pathname + window.location.search);
      } else {
        updateAnalyticsConsent(false);
      }
    } else {
      setShowBanner(true);
    }

    const handleOpenSettings = () => {
      const current = getConsent();
      setAnalyticsEnabled(!!current?.analytics);
      setShowModal(true);
    };

    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, handleOpenSettings);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true });
    updateAnalyticsConsent(true);
    initGA();
    trackPageView(window.location.pathname + window.location.search);
    setShowBanner(false);
    setShowModal(false);
  };

  const rejectAll = () => {
    saveConsent({ analytics: false });
    updateAnalyticsConsent(false);
    setShowBanner(false);
    setShowModal(false);
  };

  const savePreferences = () => {
    saveConsent({ analytics: analyticsEnabled });
    updateAnalyticsConsent(analyticsEnabled);
    if (analyticsEnabled) {
      initGA();
      trackPageView(window.location.pathname + window.location.search);
    }
    setShowBanner(false);
    setShowModal(false);
  };

  const openSettingsFromBanner = () => {
    const current = getConsent();
    setAnalyticsEnabled(!!current?.analytics);
    setShowModal(true);
  };

  if (showModal) {
    return (
      <Overlay className="modal">
        <ModalBox>
          <ModalTitle>{t('consent.modal.title')}</ModalTitle>
          <ModalText>{t('consent.modal.description')}</ModalText>

          <CategoryRow>
            <CategoryInfo>
              <CategoryTitle>{t('consent.categories.necessary.title')}</CategoryTitle>
              <CategoryText>{t('consent.categories.necessary.description')}</CategoryText>
            </CategoryInfo>
            <Switch $disabled>
              <input type="checkbox" checked disabled readOnly />
              <Slider $checked />
            </Switch>
          </CategoryRow>

          <CategoryRow>
            <CategoryInfo>
              <CategoryTitle>{t('consent.categories.analytics.title')}</CategoryTitle>
              <CategoryText>{t('consent.categories.analytics.description')}</CategoryText>
            </CategoryInfo>
            <Switch>
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
              />
              <Slider $checked={analyticsEnabled} />
            </Switch>
          </CategoryRow>

          <ModalActions>
            <TextButton type="button" onClick={rejectAll}>
              {t('consent.buttons.reject_all')}
            </TextButton>
            <SecondaryButton type="button" onClick={savePreferences}>
              {t('consent.buttons.save_preferences')}
            </SecondaryButton>
            <PrimaryButton type="button" onClick={acceptAll}>
              {t('consent.buttons.accept_all')}
            </PrimaryButton>
          </ModalActions>
        </ModalBox>
      </Overlay>
    );
  }

  if (!showBanner) {
    return null;
  }

  return (
    <Overlay>
      <Banner role="dialog" aria-label={t('consent.banner.title')}>
        <TextBlock>
          <Title>{t('consent.banner.title')}</Title>
          <Description>
            {t('consent.banner.description')}{' '}
            <Link to="/datenschutz">{t('consent.banner.privacy_link')}</Link>
          </Description>
        </TextBlock>
        <ButtonRow>
          <TextButton type="button" onClick={openSettingsFromBanner}>
            {t('consent.buttons.settings')}
          </TextButton>
          <SecondaryButton type="button" onClick={rejectAll}>
            {t('consent.buttons.reject_all')}
          </SecondaryButton>
          <PrimaryButton type="button" onClick={acceptAll}>
            {t('consent.buttons.accept_all')}
          </PrimaryButton>
        </ButtonRow>
      </Banner>
    </Overlay>
  );
};

export default ConsentManager;
